import React, { useState, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'
import {
  // safeAccess,
  isValidFileObject,
  sanitizeFileObject,
  getFileTypeFromName,
} from '../../../../../utils/fileUtils/safeFileOperations'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const SafeFilePreview = ({ file }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Sanitize file object
  const safeFile = file //sanitizeFileObject(file)
  const fileType = getFileTypeFromName(safeFile?.name)

  useEffect(() => {
    // Reset states when file changes
    setLoading(true)
    setError(false)
    setCurrentPage(1)
    setTotalPages(1)

    // Set loading to false after a brief delay if no specific processing is needed
    const timer = setTimeout(() => {
      setLoading(false)
    }, 100)

    return () => clearTimeout(timer)
  }, [safeFile])

  const renderPreview = () => {
    if (!safeFile) {
      return (
        <div className="flex flex-col items-center justify-center h-32 p-4">
          <div className="text-4xl mb-2">⚠️</div>
          <p className="text-gray-600 text-sm">No valid file selected</p>
        </div>
      )
    }

    try {
      switch (fileType) {
        case 'image':
          return (
            <div className="flex justify-center items-center h-48 p-4">
              <img
                src={
                  safeFile.previewLink ||
                  `https://via.placeholder.com/300x200/4A90E2/FFFFFF?text=${encodeURIComponent(safeFile.name)}`
                }
                alt={safeFile.name}
                className="max-w-full max-h-full object-contain rounded"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setError(true)
                  setLoading(false)
                }}
              />
            </div>
          )

        case 'pdf':
          if (safeFile.previewLink) {
            return (
              <div className="h-64 flex flex-col">
                <div className="flex-1 flex justify-center items-center">
                  <Document
                    file={safeFile.previewLink}
                    onLoadSuccess={({ numPages }) => {
                      setTotalPages(numPages || 1)
                      setLoading(false)
                    }}
                    onLoadError={() => {
                      setError(true)
                      setLoading(false)
                    }}
                    loading={
                      <div className="text-center p-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3d6be0] mb-2 mx-auto"></div>
                        <p className="text-gray-600 text-sm">Loading PDF...</p>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={currentPage}
                      width={280}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 p-2 bg-gray-50 border-t">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage <= 1}
                      className="px-2 py-1 text-xs bg-white border rounded disabled:opacity-50"
                    >
                      ←
                    </button>
                    <span className="text-xs">
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      className="px-2 py-1 text-xs bg-white border rounded disabled:opacity-50"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            )
          } else {
            return (
              <div className="flex flex-col items-center justify-center h-32 p-4">
                <div className="text-4xl mb-2">📄</div>
                <p className="text-gray-600 text-sm text-center">PDF Preview</p>
                <p className="text-gray-500 text-xs text-center">
                  Click "Full Preview" to view
                </p>
              </div>
            )
          }

        case 'document':
          return (
            <div className="flex flex-col items-center justify-center h-32 p-4">
              <div className="text-4xl mb-2">📝</div>
              <p className="text-gray-600 text-sm text-center">
                {safeFile.name}
              </p>
              <p className="text-gray-500 text-xs text-center">Word Document</p>
              <p className="text-gray-400 text-xs text-center mt-1">
                Click "Full Preview" for detailed view
              </p>
            </div>
          )

        case 'spreadsheet':
          return (
            <div className="flex flex-col items-center justify-center h-32 p-4">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-gray-600 text-sm text-center">
                {safeFile.name}
              </p>
              <p className="text-gray-500 text-xs text-center">
                Excel Spreadsheet
              </p>
              <p className="text-gray-400 text-xs text-center mt-1">
                Click "Full Preview" for detailed view
              </p>
            </div>
          )

        case 'presentation':
          return (
            <div className="flex flex-col items-center justify-center h-32 p-4">
              <div className="text-4xl mb-2">📽️</div>
              <p className="text-gray-600 text-sm text-center">
                {safeFile.name}
              </p>
              <p className="text-gray-500 text-xs text-center">
                PowerPoint Presentation
              </p>
              <p className="text-gray-400 text-xs text-center mt-1">
                Click "Full Preview" for detailed view
              </p>
            </div>
          )

        case 'video':
          return (
            <div className="flex justify-center items-center h-48 p-4">
              <video
                className="max-w-full max-h-full rounded"
                onLoadedData={() => setLoading(false)}
                onError={() => {
                  setError(true)
                  setLoading(false)
                }}
                poster={`https://via.placeholder.com/300x200/4A90E2/FFFFFF?text=Video`}
              >
                <source
                  src={safeFile.previewLink}
                  type={`video/${safeFile.name?.split('.').pop()?.toLowerCase()}`}
                />
              </video>
            </div>
          )

        case 'audio':
          return (
            <div className="flex flex-col items-center justify-center h-32 p-4">
              <div className="text-4xl mb-2">🎵</div>
              <h3 className="text-xl font-semibold mb-4">{safeFile.name}</h3>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <audio
                  controls
                  className="w-full max-w-md"
                  onLoadedData={() => setLoading(false)}
                  onError={() => {
                    setError(true)
                    setLoading(false)
                  }}
                >
                  <source
                    src={safeFile.previewLink}
                    type={`audio/${safeFile.name?.split('.').pop()?.toLowerCase()}`}
                  />
                  Your browser does not support the audio tag.
                </audio>
              </div>
            </div>
          )

        default:
          return (
            <div className="flex flex-col items-center justify-center h-32 p-4">
              <div className="text-4xl mb-2">📎</div>
              <h3 className="text-xl font-semibold mb-2">{safeFile.name}</h3>
              <p className="text-gray-600 mb-4">
                File Type: {safeFile.previewLink || 'UNKNOWN'}
              </p>
              <div className="bg-gray-50 p-4 rounded-lg max-w-md text-center">
                <p className="text-sm text-gray-700 mb-3">
                  Preview not available for this file type.
                </p>
                <p className="text-xs text-gray-500">
                  You can download the file to view its contents.
                </p>
              </div>
            </div>
          )
      }
    } catch (renderError) {
      console.error('Error rendering preview:', renderError)
      return (
        <div className="flex flex-col items-center justify-center h-32 p-4">
          <div className="text-4xl mb-2">⚠️</div>
          <p className="text-gray-600 text-sm">Error rendering preview</p>
        </div>
      )
    }
  }

  return (
    <div className="relative">
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3d6be0] mb-2 mx-auto"></div>
            <p className="text-gray-600 text-xs">Loading...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
          <div className="text-center">
            <div className="text-2xl mb-2">⚠️</div>
            <p className="text-gray-600 text-xs">Preview Error</p>
          </div>
        </div>
      )}

      <div className={`${loading ? 'opacity-30' : 'opacity-100'}`}>
        {renderPreview()}
      </div>
    </div>
  )
}

export default function SafeFilePreviewWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <SafeFilePreview {...props} />
    </ErrorBoundary>
  )
}
